import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, ArrowRight, Calendar } from 'lucide-react';

const BurnoutRecovery: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>How Close to Burnout Are You? - Burnout Recovery with Hypnotherapy</title>
        <meta name="description" content="Feeling like you're running on empty? Discover how hypnotherapy can help you reclaim your balance and recover from burnout. Book your free consultation today." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/burnout-recovery/" />
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
            How Close to Burnout Are You?
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-2">
            Feeling Like You're Running on Empty? Learn how <a href="/services" className="text-accent-600 hover:text-accent-700 underline">clinical hypnotherapy</a> can help with <a href="/anxiety-stress" className="text-accent-600 hover:text-accent-700 underline">stress management</a>.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl mx-auto">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When burnout is looming, it can feel like you're constantly treading water with no land in sight. Exhaustion becomes your baseline, motivation evaporates, and even the simplest tasks feel monumental. You might wonder, <em>Why can't I just push through like I used to?</em>
          </p>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            I understand because I've been there. Balancing the demands of a professional working life with a busy home life, all while feeling the pressure to exercise, socialise, and keep everything running smoothly at home—it's a lot. There never seem to be enough hours in the day. Maybe your family has suggested quitting your job because they see how stressed you are, but you love what you do. You just want to come home at the end of the day with some energy left for yourself and your loved ones. You want to feel that there is more to life again.
          </p>
        </div>
      </section>

      {/* Ripple Effect */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8 text-center">
            The Ripple Effect of Burnout on Your Life
          </h2>
          <p className="text-lg text-gray-700 text-center mb-8">
            Burnout doesn't just drain your energy; it seeps into every part of your life.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Work",
                description: "Productivity plummets, mistakes increase, and every deadline feels overwhelming."
              },
              {
                title: "Relationships",
                description: "You find yourself withdrawing, snapping at loved ones, or feeling too drained to connect."
              },
              {
                title: "Health",
                description: "Sleep issues, headaches, and even physical illness often appear as your body reacts to increased cortisol levels."
              },
              {
                title: "Mind",
                description: "Negative thoughts spiral, and it feels impossible to stop the worry, guilt, or sense of failure."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-3 text-lg">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-lg text-gray-700">
              Does this sound familiar? If you're nodding along, you're not alone—and you don't have to stay in this cycle.
            </p>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8 text-center">
            How Hypnotherapy Can Help You Reclaim Your Balance
          </h2>
          
          <p className="text-lg text-gray-700 text-center mb-8">
            Burnout isn't a weakness; it's your mind and body asking for change. Hypnotherapy provides a powerful, solution-focused way to:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {[
              "Quiet the mental noise and find clarity in chaos.",
              "Reconnect with your inner calm to make space for recovery.",
              "Identify patterns and triggers contributing to burnout—and shift them.",
              "Cultivate a mindset that supports balance, confidence, and well-being."
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 text-center">
            <p className="text-lg text-gray-700 leading-relaxed">
              I know how much you care about your work, your family, and yourself. Through tailored sessions, we'll work together to help you regain control, feel more like yourself again, and rediscover what it's like to end the day with something left in the tank for you and your loved ones.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-6">
            Ready to Start Your Journey to Recovery?
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
            Burnout doesn't have to define you. Take the first step towards feeling like yourself again.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href="/book"
              className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book an Initial Consultation
            </a>
            <a
              href="/book"
              className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              Request a 10-Minute Call
            </a>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Free consultation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>No obligation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Confidential & safe</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BurnoutRecovery;
